import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Download, ChevronDown, AlertTriangle, Target, ClipboardList, CheckCircle2, History, Mail, Send, SlidersHorizontal, GitCompare, ArrowRight, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { api } from "../../lib/api";

const EF = [
  ["What is happening", "what_is_happening"],
  ["Why it is happening", "why_it_is_happening"],
  ["What matters most", "what_matters_most"],
  ["What should happen next", "what_should_happen_next"],
];

const CHUNKS = ["overview", "assess", "improve", "gtm", "advanced"];
const SECTION_ORDER = [
  "bi_assessment", "opportunities", "strategy", "financial_improvement",
  "operational_improvement", "sales_marketing", "technology", "technology_ai",
  "advanced_bi", "strategic_advisory", "organizational_improvement",
];

const ctxInputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#c9a961]/60 focus:outline-none transition-colors duration-300";

const secStats = (result) => {
  const m = {};
  (result?.sections || []).forEach((s) => {
    const a = s.items.filter((i) => i.status === "analyzed").length;
    m[s.id] = { title: s.title, analyzed: a, total: s.items.length };
  });
  return m;
};
const cov = (a, t) => (t ? Math.round((a / t) * 100) : 0);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso?.slice(0, 10); } };

export default function BusinessIntelAssessment({ rows, revenue, notes, clientId, businessName }) {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: CHUNKS.length });
  const [data, setData] = useState(null);
  const [biId, setBiId] = useState(null);
  const [open, setOpen] = useState({});
  const [dl, setDl] = useState(false);
  const [history, setHistory] = useState([]);
  const [showCtx, setShowCtx] = useState(false);
  const [ctx, setCtx] = useState({ customers: "", lead_sources: "", sales_info: "", membership_info: "", extra_context: "" });
  const [emailTo, setEmailTo] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [cmpSel, setCmpSel] = useState([]);
  const [cmp, setCmp] = useState(null);
  const accRef = useRef(null);

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const setC = (k) => (e) => setCtx((c) => ({ ...c, [k]: e.target.value }));

  const fetchHistory = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data: res } = await api.get(`/expenses/business-intelligence/history/${encodeURIComponent(clientId)}`);
      setHistory(res.history || []);
    } catch { /* ignore */ }
  }, [clientId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const baseReq = () => {
    const expenses = rows
      .filter((r) => r.label.trim() && parseFloat(r.amount) > 0)
      .map((r) => ({ label: r.label.trim(), amount: parseFloat(r.amount), frequency: r.frequency, category: r.category.trim() || undefined }));
    return {
      business_name: businessName || undefined,
      monthly_revenue: revenue ? parseFloat(revenue) : undefined,
      client_id: clientId,
      expenses,
      notes: notes?.trim() || undefined,
      customers: ctx.customers.trim() || undefined,
      lead_sources: ctx.lead_sources.trim() || undefined,
      sales_info: ctx.sales_info.trim() || undefined,
      membership_info: ctx.membership_info.trim() || undefined,
      extra_context: ctx.extra_context.trim() || undefined,
    };
  };

  const merge = (res) => {
    const acc = accRef.current;
    if (res.executive_findings) acc.executive_findings = res.executive_findings;
    if (res.kpi_development) acc.kpi_development = res.kpi_development;
    if (res.recurring_revenue) acc.recurring_revenue = res.recurring_revenue;
    if (res.sections) {
      const map = new Map(acc.sections.map((s) => [s.id, s]));
      res.sections.forEach((s) => map.set(s.id, s));
      acc.sections = [...map.values()].sort((a, b) => SECTION_ORDER.indexOf(a.id) - SECTION_ORDER.indexOf(b.id));
      if (!Object.keys(open).length && acc.sections[0]) setOpen({ [acc.sections[0].id]: true });
    }
    setData({ ...acc, stats: null });
  };

  const generate = async () => {
    const base = baseReq();
    if (!base.expenses.length) { toast.error("Add at least one expenditure first."); return; }
    setLoading(true);
    setCmp(null); setCompareMode(false); setCmpSel([]);
    setProgress({ done: 0, total: CHUNKS.length });
    accRef.current = { executive_findings: {}, kpi_development: [], sections: [] };
    setData({ executive_findings: {}, kpi_development: [], sections: [], stats: null });
    setBiId(null);
    setOpen({});

    await Promise.allSettled(
      CHUNKS.map((chunk) =>
        api.post("/expenses/business-intelligence/chunk", { ...base, chunk })
          .then((r) => merge(r.data))
          .catch(() => toast.error(`One section (${chunk}) didn't generate.`))
          .finally(() => setProgress((p) => ({ ...p, done: p.done + 1 })))
      )
    );

    try {
      const { data: saved } = await api.post("/expenses/business-intelligence/save", {
        client_id: clientId,
        business_name: businessName || undefined,
        monthly_revenue: revenue ? parseFloat(revenue) : undefined,
        notes: notes?.trim() || undefined,
        result: accRef.current,
      });
      setBiId(saved.id);
      setData((d) => ({ ...d, stats: saved.result.stats }));
      toast.success("Business Intelligence Assessment ready.");
      fetchHistory();
    } catch {
      toast.error("Assessment generated but could not be saved.");
    } finally {
      setLoading(false);
    }
  };

  const reopen = async (id) => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/expenses/business-intelligence/${id}`);
      setData(res.result);
      setBiId(res.id);
      setOpen(res.result.sections?.[0] ? { [res.result.sections[0].id]: true } : {});
      toast.success("Assessment loaded.");
    } catch {
      toast.error("Could not load that assessment.");
    } finally {
      setLoading(false);
    }
  };

  const onHistoryClick = async (h) => {
    if (!compareMode) return reopen(h.id);
    setCmpSel((sel) => {
      if (sel.includes(h.id)) return sel.filter((x) => x !== h.id);
      if (sel.length >= 2) return [sel[1], h.id];
      return [...sel, h.id];
    });
  };

  const runCompare = async () => {
    if (cmpSel.length !== 2) { toast.error("Pick two assessments to compare."); return; }
    try {
      const [ra, rb] = await Promise.all(cmpSel.map((id) => api.get(`/expenses/business-intelligence/${id}`)));
      const A = ra.data, B = rb.data;
      const older = new Date(A.created_at) <= new Date(B.created_at) ? A : B;
      const newer = older === A ? B : A;
      setCmp({
        older: { created_at: older.created_at, result: older.result || {} },
        newer: { created_at: newer.created_at, result: newer.result || {} },
      });
    } catch {
      toast.error("Could not load both assessments.");
    }
  };

  const downloadPdf = async () => {
    if (!biId) return;
    setDl(true);
    try {
      const res = await api.get(`/expenses/business-intelligence/${biId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "elevare-bi-assessment.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF."); }
    finally { setDl(false); }
  };

  const emailReport = async () => {
    if (!biId) return;
    if (!emailTo.trim()) { toast.error("Enter the client's email."); return; }
    setEmailing(true);
    try {
      await api.post(`/expenses/business-intelligence/${biId}/email`, { to_email: emailTo.trim() });
      toast.success(`Assessment emailed to ${emailTo.trim()}.`);
      setEmailTo("");
    } catch { toast.error("Could not send the email."); }
    finally { setEmailing(false); }
  };

  const Delta = ({ d }) => {
    if (d > 0) return <span className="text-[#10b981] flex items-center gap-0.5"><ArrowUp className="h-3 w-3" />{d}</span>;
    if (d < 0) return <span className="text-[#ef4444] flex items-center gap-0.5"><ArrowDown className="h-3 w-3" />{Math.abs(d)}</span>;
    return <span className="text-white/40 flex items-center gap-0.5"><Minus className="h-3 w-3" /></span>;
  };

  return (
    <div data-testid="bi-assessment" className="rounded-2xl border border-[#c9a961]/30 bg-[#c9a961]/[0.06] p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-[#c9a961] flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Full Business Intelligence Assessment
          </span>
          <p className="mt-2 text-sm text-white/55 max-w-xl">
            Runs across financial, operational, sales, technology and strategic dimensions — sections
            stream in as they're ready. Items with missing data are flagged.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {data && biId && (
            <button data-testid="bi-download-pdf" onClick={downloadPdf} disabled={dl}
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:border-white/50 transition-colors duration-300 disabled:opacity-60">
              {dl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
            </button>
          )}
          <button data-testid="bi-generate" onClick={generate} disabled={loading}
            className="flex items-center gap-2 rounded-full bg-[#c9a961] text-black px-5 py-2.5 text-sm font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {progress.done}/{progress.total}</> : <><Sparkles className="h-4 w-4" /> {biId ? "Regenerate" : "Generate Assessment"}</>}
          </button>
        </div>
      </div>

      {/* Optional business context */}
      <div className="mt-5">
        <button data-testid="bi-context-toggle" onClick={() => setShowCtx((s) => !s)}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55 hover:text-white transition-colors duration-300">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Add business context (optional)
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${showCtx ? "rotate-180" : ""}`} />
        </button>
        {showCtx && (
          <div data-testid="bi-context-fields" className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="bi-ctx-customers" className={ctxInputCls} value={ctx.customers} onChange={setC("customers")} placeholder="Customers (e.g. 1,200 active · ~40 new/mo · 30% repeat)" />
            <input data-testid="bi-ctx-leads" className={ctxInputCls} value={ctx.lead_sources} onChange={setC("lead_sources")} placeholder="Lead sources (e.g. Google Ads 50% · referrals 30%)" />
            <input data-testid="bi-ctx-sales" className={ctxInputCls} value={ctx.sales_info} onChange={setC("sales_info")} placeholder="Sales (e.g. avg ticket $85 · 600 orders/mo)" />
            <input data-testid="bi-ctx-membership" className={ctxInputCls} value={ctx.membership_info} onChange={setC("membership_info")} placeholder="Recurring revenue (e.g. $22k MRR · 220 members · +15/mo · 4% churn)" />
            <input data-testid="bi-ctx-extra" className={ctxInputCls} value={ctx.extra_context} onChange={setC("extra_context")} placeholder="Anything else worth knowing" />
          </div>
        )}
      </div>

      {/* Past assessments + compare */}
      {history.length > 0 && (
        <div data-testid="bi-history" className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/45 flex items-center gap-2">
              <History className="h-3.5 w-3.5" /> Past assessments
            </span>
            {history.length > 1 && (
              <button data-testid="bi-compare-toggle"
                onClick={() => { setCompareMode((m) => !m); setCmpSel([]); setCmp(null); }}
                className={`text-[11px] uppercase tracking-[0.15em] flex items-center gap-1.5 rounded-full border px-3 py-1 transition-colors duration-300 ${compareMode ? "border-[#c9a961] text-[#c9a961]" : "border-white/15 text-white/60 hover:text-white"}`}>
                <GitCompare className="h-3.5 w-3.5" /> {compareMode ? "Cancel compare" : "Compare"}
              </button>
            )}
          </div>
          {compareMode && (
            <p className="mt-2 text-[11px] text-white/50">Select two assessments to compare ({cmpSel.length}/2 chosen).</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {history.map((h) => {
              const st = h.result?.stats || {};
              const active = compareMode ? cmpSel.includes(h.id) : h.id === biId;
              return (
                <button key={h.id} data-testid={`bi-history-${h.id}`} onClick={() => onHistoryClick(h)}
                  className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-300 flex items-center gap-2 ${active ? "border-[#c9a961] bg-[#c9a961]/15 text-white" : "border-white/15 text-white/70 hover:border-[#c9a961]/50 hover:text-white"}`}
                  title={`${st.analyzed || 0} analyzed · ${st.needs_data || 0} need data`}>
                  {fmtDate(h.created_at)}
                  {typeof st.total_items === "number" && (
                    <span className="text-[#10b981]">{st.analyzed}<span className="text-white/30">/{st.total_items}</span></span>
                  )}
                </button>
              );
            })}
          </div>
          {compareMode && (
            <button data-testid="bi-compare-run" onClick={runCompare} disabled={cmpSel.length !== 2}
              className="mt-3 rounded-full bg-[#c9a961] text-black px-4 py-1.5 text-xs font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-50 flex items-center gap-2">
              <GitCompare className="h-3.5 w-3.5" /> Compare selected
            </button>
          )}
        </div>
      )}

      {/* Compare view */}
      {cmp && (() => {
        const sa = secStats(cmp.older.result), sb = secStats(cmp.newer.result);
        const oa = cmp.older.result.stats || {}, ob = cmp.newer.result.stats || {};
        const covA = cov(oa.analyzed, oa.total_items), covB = cov(ob.analyzed, ob.total_items);
        return (
          <div data-testid="bi-compare-view" className="mt-6 rounded-2xl border border-[#c9a961]/25 bg-white/[0.03] p-6">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-[#c9a961] flex items-center gap-2"><GitCompare className="h-4 w-4" /> Assessment comparison</span>
              <button onClick={() => setCmp(null)} className="text-xs text-white/50 hover:text-white">Close</button>
            </div>
            <div className="mt-4 grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
              <div className="text-center">
                <p className="text-[11px] uppercase tracking-[0.15em] text-white/45">{fmtDate(cmp.older.created_at)}</p>
                <p className="mt-1 font-head text-3xl text-white">{covA}%</p>
                <p className="text-[11px] text-white/40">{oa.analyzed}/{oa.total_items} analyzed</p>
              </div>
              <ArrowRight className="h-5 w-5 text-white/30 mx-auto" />
              <div className="text-center">
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#c9a961]">{fmtDate(cmp.newer.created_at)}</p>
                <p className="mt-1 font-head text-3xl text-white flex items-center justify-center gap-2">{covB}% <span className="text-sm"><Delta d={covB - covA} /></span></p>
                <p className="text-[11px] text-white/40">{ob.analyzed}/{ob.total_items} analyzed</p>
              </div>
            </div>
            <div className="mt-6 space-y-2">
              {SECTION_ORDER.filter((id) => sa[id] || sb[id]).map((id) => {
                const A = sa[id] || { analyzed: 0, total: 0 }, B = sb[id] || { analyzed: 0, total: 0 };
                const cA = cov(A.analyzed, A.total), cB = cov(B.analyzed, B.total);
                return (
                  <div key={id} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-2">
                    <span className="text-white/70">{(sb[id] || sa[id]).title}</span>
                    <span className="flex items-center gap-3 text-xs shrink-0">
                      <span className="text-white/40 tabular-nums">{cA}%</span>
                      <ArrowRight className="h-3 w-3 text-white/20" />
                      <span className="text-white tabular-nums">{cB}%</span>
                      <span className="w-10 text-right"><Delta d={cB - cA} /></span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {data && !cmp && (
        <div className="mt-6 space-y-6">
          {/* Stats + email */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap gap-3 text-xs">
              {data.stats ? (
                <>
                  <span className="rounded-full bg-white/[0.06] border border-white/10 px-3 py-1 text-white/70">{data.stats.total_items} areas assessed</span>
                  <span className="rounded-full bg-[#10b981]/10 border border-[#10b981]/30 px-3 py-1 text-[#10b981]">{data.stats.analyzed} analyzed</span>
                  {data.stats.needs_data > 0 && (
                    <span className="rounded-full bg-[#8a5c3a]/15 border border-[#8a5c3a]/40 px-3 py-1 text-[#d0a06a]">{data.stats.needs_data} need more data</span>
                  )}
                </>
              ) : loading ? (
                <span className="rounded-full bg-white/[0.06] border border-white/10 px-3 py-1 text-white/50 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Streaming sections… {progress.done}/{progress.total}</span>
              ) : null}
            </div>
            {biId && (
              <div className="flex items-center gap-2">
                <div className="relative flex-1 lg:w-64">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                  <input data-testid="bi-email-input" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="client@email.com" className={`${ctxInputCls} pl-9`} />
                </div>
                <button data-testid="bi-email-send" onClick={emailReport} disabled={emailing}
                  className="shrink-0 rounded-full bg-[#c9a961] text-black px-4 py-2 text-sm font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60 flex items-center gap-2">
                  {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Email
                </button>
              </div>
            )}
          </div>

          {/* Recurring Revenue Health */}
          {data.recurring_revenue && (data.recurring_revenue.assessment || data.recurring_revenue.score != null) && (
            <div data-testid="bi-recurring-revenue" className="rounded-2xl border border-[#c9a961]/30 bg-[#1e3d3a] p-6">
              <div className="flex items-start justify-between gap-4">
                <span className="text-xs uppercase tracking-[0.2em] text-[#c9a961]">Recurring Revenue Health</span>
                {data.recurring_revenue.score != null && (
                  <div className="text-right shrink-0">
                    <span className="font-head text-3xl font-light text-white tabular-nums">{data.recurring_revenue.score}</span>
                    <span className="text-white/40 text-sm">/100</span>
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2">
                {[["MRR", data.recurring_revenue.mrr], ["Members", data.recurring_revenue.members], ["Growth", data.recurring_revenue.growth], ["Churn", data.recurring_revenue.churn], ["LTV", data.recurring_revenue.ltv]].filter(([, v]) => (v || "").trim()).map(([l, v]) => (
                  <div key={l}>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#c9a961]">{l}</p>
                    <p className="text-sm text-white font-medium">{v}</p>
                  </div>
                ))}
              </div>
              {(data.recurring_revenue.assessment || "").trim() && (
                <p className="mt-4 text-sm leading-relaxed text-white/70">{data.recurring_revenue.assessment}</p>
              )}
            </div>
          )}

          {/* Executive Findings */}
          {data.executive_findings && Object.values(data.executive_findings).some(Boolean) && (
            <div data-testid="bi-exec-findings" className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <span className="text-xs uppercase tracking-[0.2em] text-white/50 flex items-center gap-2"><Target className="h-4 w-4" /> Executive Findings</span>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {EF.map(([label, key]) => data.executive_findings[key] ? (
                  <div key={key} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#c9a961]">{label}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/75">{data.executive_findings[key]}</p>
                  </div>
                ) : null)}
              </div>
            </div>
          )}

          {/* KPI Development */}
          {data.kpi_development?.length > 0 && (
            <div data-testid="bi-kpis" className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <span className="text-xs uppercase tracking-[0.2em] text-white/50 flex items-center gap-2"><ClipboardList className="h-4 w-4" /> KPI Development — the numbers to watch</span>
              <div className="mt-4 space-y-3">
                {data.kpi_development.map((k, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c9a961]" />
                    <p className="text-sm text-white/75 leading-relaxed">
                      <span className="text-white font-medium">{k.kpi}</span>
                      {k.why ? <span className="text-white/55"> — {k.why}</span> : null}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sections accordion */}
          <div className="space-y-3">
            {data.sections?.map((sec) => {
              const nd = sec.items.filter((i) => i.status === "needs_data").length;
              const isOpen = !!open[sec.id];
              return (
                <div key={sec.id} data-testid={`bi-section-${sec.id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
                  <button data-testid={`bi-section-toggle-${sec.id}`} onClick={() => toggle(sec.id)}
                    className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-white/[0.02] transition-colors duration-300">
                    <span className="font-head text-lg text-white">{sec.title}</span>
                    <span className="flex items-center gap-3 shrink-0">
                      {nd > 0 && <span className="text-[11px] text-[#d0a06a] flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {nd}</span>}
                      <ChevronDown className={`h-4 w-4 text-white/50 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
                      {sec.items.map((it, i) => (
                        <div key={i} className="flex items-start gap-3">
                          {it.status === "needs_data" ? <AlertTriangle className="h-4 w-4 text-[#d0a06a] shrink-0 mt-0.5" /> : <CheckCircle2 className="h-4 w-4 text-[#10b981] shrink-0 mt-0.5" />}
                          <div>
                            <p className="text-sm text-white font-medium flex items-center gap-2">
                              {it.label}
                              {it.status === "needs_data" && <span className="text-[10px] uppercase tracking-[0.15em] text-[#d0a06a] border border-[#8a5c3a]/40 rounded-full px-2 py-0.5">needs data</span>}
                            </p>
                            <p className={`mt-1 text-sm leading-relaxed ${it.status === "needs_data" ? "text-[#d0a06a]/80" : "text-white/60"}`}>{it.finding}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {loading && !data.stats && (
              <div className="rounded-2xl border border-dashed border-white/10 p-5 flex items-center gap-3 text-sm text-white/45">
                <Loader2 className="h-4 w-4 animate-spin" /> More sections generating…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
